import React, { useEffect, useState, useMemo } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SuiteFormDialog } from "@/foundation/ui/SuiteFormDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MONTHS_IT } from "@/lib/constants";
import { Plus, Pencil, Trash2, Building2, Link2, Copy, Palette, Search, X, FileText, Clock, UserCog, Info } from "lucide-react";
import { SuiteCard, SuiteButton, SuiteEyebrow } from "@/components/suite";
import { sortCompaniesIt, filterCompanies } from "@/lib/sortCompanies";
import { mmddToItalian, italianToMmdd } from "@/lib/dates";
import { useLocation, useNavigate } from "react-router-dom";
import CompanyAccessSection from "@/components/access/CompanyAccessSection";
import CompanyContractsPanel from "@/components/settings/CompanyContractsPanel";
import { SuiteTabs, SuiteTab, SuiteTabsList, SuiteTabsContent } from "@/foundation/ui/SuiteTabs";
import { useAuth } from "@/contexts/AuthContext";
import { useEffectiveRole } from "@/foundation/context";
import { CompensationPeriodFields } from "@/components/companies/CompensationPeriodFields";
import { compensationPeriodLabel } from "@/lib/constants";
import { TimesheetCompanySettings } from "@/components/timesheet/TimesheetCompanySettings";
import { DurationFormatSelect, DurationInput, DurationLabel, announceCompanyDurationFormat } from "@/foundation/duration";
import { AnagraficaFiscaleSection, useAnagraficaValidation } from "@/foundation/anagrafica";

const empty = () => ({
  name: "", tax_code: "", vat: "", address: "", email: "", phone: "", notes: "",
  pec: "", rea_number: "",           // v0.99.85+
  cap: "", comune: "", provincia: "",// v1.3.4+ · sede strutturata (GI Soggetti-ready)
  bank_hours_enabled: false,          // v0.99.85+: default "Nessuna" per NUOVE aziende
  compensation_period_months: 6, semester_start_month: 1,
  active_month: new Date().getMonth() + 1, active_year: new Date().getFullYear(),
  santo_patrono_date: "", santo_patrono_name: "",
  logo_url: "", primary_color: "", portal_slug: "",
  // v1.3.5+ · campi backend precedentemente orfani in UI (P3)
  consultant_report_mode: "consuntivo_effettivo",
  attendance_autofill_mode: "ask",
  duration_format: "decimal",        // M-UX-05
  timesheet_prefill_mode: "empty",   // M-WF-01
  overtime_allocation: null,         // M-WF-01 F2
  timesheet_next_mode: "ask",        // M-WF-01 F2
  // v1.3.5+ · portal_notes separato da notes anagrafiche (P0.1).
  // Memorizzato in custom_settings per preservare la retrocompatibilita' del
  // modello backend: il tipo del campo e' identico a tutti gli altri extra.
  custom_settings: {
    portal_title: "", portal_subtitle: "", secondary_color: "",
    login_background_url: "", anomaly_threshold_hours: "", portal_notes: "",
  },
  notify_external_consultant: false,
});

// Genera slug da nome (kebab-case, senza accenti)
function toSlug(str) {
  if (!str) return "";
  return String(str)
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function Companies() {
  const { user } = useAuth();
  const effectiveRole = useEffectiveRole();
  const navigate = useNavigate();
  useLocation(); // ensure re-render on route change (used to close dialog when navigating away)
  const isExternalConsultant = effectiveRole === "external_consultant";
  const isCompanyUser = ["client", "company_admin", "company_responsible"].includes(effectiveRole);
  const canCreateOrDelete = ["admin", "super_admin"].includes(effectiveRole);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [initialForm, setInitialForm] = useState(empty());
  const [editingId, setEditingId] = useState(null);
  // v0.99.30-c1 · Punto 4: ricerca client-side su nome / P.IVA / C.F. / email.
  const [search, setSearch] = useState("");
  // v0.99.30-c2 · Filtro banca ore attiva/non attiva.
  const [bankFilter, setBankFilter] = useState("all"); // "all" | "on" | "off"
  // v0.99.30-c1 · Punto 3: input Santo Patrono in formato italiano "GG-MM".
  // `patronoDisplay` = valore visibile nel form; `patronoError` = errore validazione.
  const [patronoDisplay, setPatronoDisplay] = useState("");
  const [patronoError, setPatronoError] = useState(null);
  const [activeCompanyTab, setActiveCompanyTab] = useState("anagrafica");

  const isDirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(initialForm),
    [form, initialForm]
  );

  // P-UX-07 · validazioni Foundation all'uscita dal campo (candidata BR-GIP-003).
  const validation = useAnagraficaValidation(form);
  const fieldErrors = validation.visible;
  const anagraficaHasError = Object.values(fieldErrors).some(Boolean);

  // P5 · dirty indicator per tab (confronto per-sezione di campi).
  const tabDirty = useMemo(() => {
    const diff = (keys) => keys.some((k) => (form[k] ?? "") !== (initialForm[k] ?? ""));
    const diffCS = (keys) => keys.some((k) =>
      (form.custom_settings?.[k] ?? "") !== (initialForm.custom_settings?.[k] ?? "")
    );
    return {
      anagrafica: diff([
        "name","tax_code","vat","pec","rea_number","email","phone",
        "address","cap","comune","provincia","notes","notify_external_consultant",
      ]),
      operativita: diff([
        "bank_hours_enabled","compensation_period_months","semester_start_month",
        "active_month","active_year","santo_patrono_date","santo_patrono_name",
        "consultant_report_mode","attendance_autofill_mode","duration_format","timesheet_prefill_mode","overtime_allocation","timesheet_next_mode",
      ]) || diffCS(["anomaly_threshold_hours"]),
      portale: diff(["logo_url","primary_color","portal_slug"]) ||
        diffCS(["portal_title","portal_subtitle","secondary_color","login_background_url","portal_notes"]),
    };
  }, [form, initialForm]);

  // Ordinamento italiano + filtro combinato (search + banca ore).
  const displayedItems = useMemo(
    () => sortCompaniesIt(filterCompanies(items, { query: search, bankHours: bankFilter })),
    [items, search, bankFilter]
  );

  const load = async () => {
    try {
      const { data } = await api.get("/companies");
      setItems(sortCompaniesIt(data));
    }
    catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    const fresh = empty();
    setForm(fresh); setInitialForm(fresh); setEditingId(null); setOpen(true);
    setPatronoDisplay(""); setPatronoError(null);
    setActiveCompanyTab("anagrafica"); // P0.2 · reset tab ad ogni apertura
    validation.reset();
  };
  const openEdit = (c) => {
    const merged = {
      ...empty(),
      ...c,
      custom_settings: { ...empty().custom_settings, ...(c.custom_settings || {}) },
    };
    setForm(merged); setInitialForm(merged); setEditingId(c.id); setOpen(true);
    // Converte "MM-DD" (storage) → "GG-MM" (UI IT).
    setPatronoDisplay(mmddToItalian(merged.santo_patrono_date || ""));
    setPatronoError(null);
    setActiveCompanyTab("anagrafica"); // P0.2 · reset tab ad ogni apertura
    validation.reset();
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nome azienda obbligatorio"); setActiveCompanyTab("anagrafica"); return; }
    // v0.99.85+: PEC obbligatoria in creazione (per aziende esistenti la PATCH
    // richiede PEC solo se il campo viene esplicitamente inviato non vuoto).
    if (!editingId && !(form.pec || "").trim()) {
      toast.error("PEC obbligatoria per le nuove aziende");
      setActiveCompanyTab("anagrafica");
      return;
    }
    if (editingId && form.pec !== undefined && !(form.pec || "").trim()) {
      toast.error("PEC obbligatoria");
      setActiveCompanyTab("anagrafica");
      return;
    }
    // P1 · validazioni client-side bloccanti al submit
    const firstErr = validation.firstError;
    if (firstErr) {
      validation.revealAll();
      toast.error(firstErr);
      setActiveCompanyTab("anagrafica");
      return;
    }
    // Validazione Santo Patrono: se compilato deve essere GG-MM valido.
    if (patronoDisplay && patronoError) {
      toast.error(patronoError);
      setActiveCompanyTab("operativita");
      return;
    }
    // Se lo slug è vuoto, deriva automaticamente dal nome
    const payload = { ...form };
    if (!payload.portal_slug) payload.portal_slug = toSlug(payload.name);
    // Santo Patrono: converti GG-MM (UI) → MM-DD (storage). Se vuoto → null.
    if (patronoDisplay) {
      const conv = italianToMmdd(patronoDisplay);
      if (conv.error) { toast.error(conv.error); return; }
      payload.santo_patrono_date = conv.value || null;
    } else {
      payload.santo_patrono_date = null;
    }
    // custom_settings: elimina i valori vuoti
    if (payload.custom_settings) {
      const cs = Object.fromEntries(
        Object.entries(payload.custom_settings).filter(([, v]) => v !== "" && v != null)
      );
      payload.custom_settings = Object.keys(cs).length ? cs : null;
    }
    try {
      if (editingId) await api.patch(`/companies/${editingId}`, payload);
      else await api.post("/companies", payload);
      if (editingId) announceCompanyDurationFormat(editingId, payload.duration_format);
      toast.success("Salvato");
      // reset dirty prima di chiudere
      setInitialForm(form);
      setOpen(false);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const copyPortalLink = (slug) => {
    if (!slug) { toast.error("Slug non impostato"); return; }
    const url = `${window.location.origin}/login/${slug}`;
    navigator.clipboard.writeText(url)
      .then(() => toast.success(`Link portale copiato: ${url}`))
      .catch(() => toast.error("Copia non riuscita"));
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare l'azienda e tutti i suoi dipendenti/presenze?")) return;
    try { await api.delete(`/companies/${id}`); toast.success("Eliminata"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-6 max-w-[1400px]">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <SuiteEyebrow icon={Building2}>Anagrafica</SuiteEyebrow>
          <h1 className="text-3xl font-bold tracking-tight mt-1 text-suite-neutral-800" style={{ fontFamily: "Manrope" }}>{isCompanyUser ? "Anagrafica azienda" : "Aziende"}</h1>
          <p className="text-sm text-suite-neutral-500 mt-1">{isCompanyUser ? "Consulta e aggiorna tutti i dati della tua azienda." : "Gestisci le aziende clienti dello studio."}</p>
        </div>
        {canCreateOrDelete && (
          <SuiteButton onClick={openNew} data-testid="new-company-btn" icon={Plus}>
            Nuova azienda
          </SuiteButton>
        )}
      </div>

      <SuiteCard padding="none" testid="companies-list-card">
        {/* Barra ricerca — filtra per ragione sociale / P.IVA / C.F. / email */}
        <div className="px-4 py-3 border-b border-suite-neutral-200/70 flex items-center gap-2" data-testid="companies-filter-bar">
          <div className="relative flex-1 max-w-md">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-suite-neutral-400 pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per ragione sociale, P.IVA, codice fiscale o email…"
              className="h-8 pl-8 pr-8 text-xs"
              data-testid="companies-search-input"
            />
            {search && (
              <button type="button" onClick={() => setSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-suite-neutral-400 hover:text-suite-neutral-700"
                      data-testid="companies-search-clear" title="Pulisci ricerca">
                <X size={12} />
              </button>
            )}
          </div>
          <Select value={bankFilter} onValueChange={setBankFilter}>
            <SelectTrigger className="h-8 w-[210px] text-xs" data-testid="companies-bankhours-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte (banca ore)</SelectItem>
              <SelectItem value="on">Banca ore attiva</SelectItem>
              <SelectItem value="off">Banca ore non attiva</SelectItem>
            </SelectContent>
          </Select>
          {(search || bankFilter !== "all" || items.length !== displayedItems.length) && (
            <div className="ml-auto text-xs text-suite-neutral-500" data-testid="companies-results-count">
              <span className="font-mono font-semibold text-suite-neutral-800">{displayedItems.length}</span> di {items.length}
            </div>
          )}
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-widest">Azienda</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">P.IVA / C.F.</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Periodo</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Mese attivo</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayedItems.map((c) => (
              <TableRow key={c.id} className="hover:bg-suite-neutral-50" data-testid={`company-row-${c.id}`}>
                <TableCell>
                  <button type="button"
                    className="flex items-center gap-2 text-left hover:opacity-80 cursor-pointer w-full"
                    onClick={() => navigate(`/aziende/${c.id}`)}
                    data-testid={`company-row-name-${c.id}`}>
                    <div className="w-8 h-8 rounded-md bg-suite-primary-light text-suite-primary flex items-center justify-center">
                      <Building2 size={14} />
                    </div>
                    <div>
                      <div className="font-semibold text-suite-primary hover:underline">{c.name}</div>
                      <div className="text-xs text-suite-neutral-500">{c.email}</div>
                    </div>
                  </button>
                </TableCell>
                <TableCell className="text-sm text-suite-neutral-600">{c.vat || c.tax_code || "—"}</TableCell>
                <TableCell>
                  {c.bank_hours_enabled === false ? (
                    <span className="text-xs text-suite-neutral-400" data-testid={`company-bank-off-${c.id}`}>
                      Nessuna banca ore
                    </span>
                  ) : (
                    <span data-testid={`company-bank-on-${c.id}`}>{compensationPeriodLabel(c.compensation_period_months)}</span>
                  )}
                </TableCell>
                <TableCell>{MONTHS_IT[c.active_month - 1]} {c.active_year}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => navigate(`/aziende/${c.id}`)}
                          title={isExternalConsultant ? "Consulta anagrafica completa" : "Modifica azienda"}
                          data-testid={`edit-company-${c.id}`}><Pencil size={16} /></Button>
                  {canCreateOrDelete && (
                    <Button variant="ghost" size="icon" onClick={() => del(c.id)} className="text-suite-error hover:bg-suite-error-light" data-testid={`delete-company-${c.id}`}><Trash2 size={16} /></Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-suite-neutral-400 py-8">Nessuna azienda</TableCell></TableRow>
            )}
            {items.length > 0 && displayedItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-suite-neutral-400 py-8" data-testid="companies-empty-filtered">
                  Nessun risultato per &quot;{search}&quot;
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </SuiteCard>

      <SuiteFormDialog
        open={open}
        onOpenChange={setOpen}
        isDirty={isDirty}
        size="xl"
        title={
          isExternalConsultant
            ? `Anagrafica azienda${form.name ? ` — ${form.name}` : ""}`
            : (editingId
                ? `Modifica azienda${form.name ? ` — ${form.name}` : ""}`  // P6.1 breadcrumb
                : "Nuova azienda")
        }
        description={isExternalConsultant ? "Consultazione completa in sola lettura." : "Le modifiche saranno salvate solo cliccando su Salva."}
        onSubmit={save}
        hideSubmit={isExternalConsultant}
        testid="company-form-dialog"
      >
        <div data-testid="company-form" className="min-w-0 overflow-hidden">
          <SuiteTabs value={activeCompanyTab} onValueChange={setActiveCompanyTab} className="w-full">
            {/* P5 · valorizzazione prop semantiche SuiteTab (icon/dirty/status) + P6.3 */}
            <SuiteTabsList className="mb-4">
              <SuiteTab
                value="anagrafica"
                data-testid="company-tab-anagrafica"
                icon={<Building2 size={14} />}
                dirty={tabDirty.anagrafica}
                status={anagraficaHasError ? "error" : null}
              >Anagrafica</SuiteTab>
              <SuiteTab
                value="operativita"
                data-testid="company-tab-operativita"
                icon={<Clock size={14} />}
                dirty={tabDirty.operativita}
              >Operatività</SuiteTab>
              <SuiteTab
                value="contratti"
                data-testid="company-tab-contratti"
                icon={<FileText size={14} />}
              >Contratti e istituti</SuiteTab>
              <SuiteTab
                value="accessi"
                data-testid="company-tab-accessi"
                icon={<UserCog size={14} />}
                disabled={!editingId || isExternalConsultant}
              >Accessi aziendali</SuiteTab>
              <SuiteTab
                value="portale"
                data-testid="company-tab-portale"
                icon={<Palette size={14} />}
                dirty={tabDirty.portale}
              >Branding / Portale</SuiteTab>
            </SuiteTabsList>

            {/* ============== TAB ANAGRAFICA ============== */}
            <SuiteTabsContent value="anagrafica" className="mt-0">
              <fieldset disabled={isExternalConsultant} className="space-y-5 disabled:opacity-100">
                <AnagraficaFiscaleSection form={form} setForm={setForm} validation={validation}
                  readOnly={isExternalConsultant} pecRequired tid="company-form" />

                {/* Note anagrafiche (P0.1 · separate da Note Portale) */}
                <div>
                  <Label>Note anagrafiche</Label>
                  <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="company-form-notes" />
                </div>
              </fieldset>
            </SuiteTabsContent>

            {/* ============== TAB OPERATIVITA' (ex Banca ore e calendario · P2) ============== */}
            <SuiteTabsContent value="operativita" className="mt-0">
              <fieldset disabled={isExternalConsultant} className="space-y-5 disabled:opacity-100">
                {/* Config banca ore */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Gestione banca ore</Label>
                    <Select
                      value={form.bank_hours_enabled ? "on" : "off"}
                      onValueChange={(v) => setForm({ ...form, bank_hours_enabled: v === "on" })}
                    >
                      <SelectTrigger data-testid="company-form-bank-hours"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="on">Attiva</SelectItem>
                        <SelectItem value="off">Nessuna</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="text-xs text-suite-neutral-500 mt-1">
                      Se disattivata, KPI e report banca ore non compaiono per questa azienda. I valori di periodo e inizio semestre restano conservati.
                    </div>
                  </div>
                  <CompensationPeriodFields form={form} setForm={setForm} disabled={!form.bank_hours_enabled} />
                  <div>
                    <DurationLabel data-testid="company-form-anomaly-threshold">Soglia anomalia saldo (h)</DurationLabel>
                    <DurationInput allowNegative placeholder="-20"
                      value={form.custom_settings?.anomaly_threshold_hours ?? ""}
                      onCommit={(n) => setForm((f) => ({ ...f, custom_settings: { ...f.custom_settings, anomaly_threshold_hours: n ?? "" } }))}
                      data-testid="company-form-anomaly-threshold"
                    />
                    <div className="text-[11px] text-suite-neutral-500 mt-1">
                      Default: −20 h. Notifica all&apos;admin quando il saldo progressivo di un dipendente scende sotto questa soglia.
                    </div>
                  </div>
                </div>

                {/* Calendario operativo */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Mese attivo</Label>
                    <Select value={String(form.active_month)} onValueChange={(v) => setForm({ ...form, active_month: Number(v) })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {MONTHS_IT.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Anno attivo</Label>
                    <Input type="number" value={form.active_year} onChange={(e) => setForm({ ...form, active_year: Number(e.target.value) })} />
                  </div>
                  <div>
                    <Label>Data Santo Patrono (GG-MM)</Label>
                    <Input placeholder="es. 20-01" value={patronoDisplay} maxLength={5}
                      onChange={(e) => { setPatronoDisplay(e.target.value); setPatronoError(italianToMmdd(e.target.value).error); }}
                      data-testid="company-form-patrono-date"
                    />
                    <div className="text-[11px] text-suite-neutral-500 mt-1">Formato giorno-mese (es. 20 gennaio → 20-01)</div>
                    {patronoError && <div className="text-xs text-suite-error mt-1">{patronoError}</div>}
                  </div>
                  <div>
                    <Label>Nome Santo Patrono</Label>
                    <Input placeholder="es. San Vitale Martire" value={form.santo_patrono_name || ""} onChange={(e) => setForm({ ...form, santo_patrono_name: e.target.value })} data-testid="company-form-patrono-name" />
                  </div>
                </div>

                {/* Preferenze operative (P3 · campi backend precedentemente orfani) */}
                <div className="rounded-lg border border-suite-neutral-200 p-4 space-y-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-suite-neutral-800">
                    <Info size={14} className="text-suite-primary" /> Preferenze operative
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <DurationFormatSelect value={form.duration_format} disabled={isExternalConsultant}
                      onChange={(v) => setForm({ ...form, duration_format: v })} />
                    <TimesheetCompanySettings form={form} setForm={setForm} disabled={isExternalConsultant} />
                    <div>
                      <Label>Compilazione automatica cartellino</Label>
                      <Select value={form.attendance_autofill_mode || "ask"} onValueChange={(v) => setForm({ ...form, attendance_autofill_mode: v })}>
                        <SelectTrigger data-testid="company-form-attendance-autofill"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ask">Chiedi all&apos;operatore</SelectItem>
                          <SelectItem value="never">Mai (compilazione manuale)</SelectItem>
                          <SelectItem value="always_theoretical">Sempre con orario teorico contrattuale</SelectItem>
                          <SelectItem value="always_profile">Sempre con profilo orario dipendente</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="text-[11px] text-suite-neutral-500 mt-1">
                        Governa il comportamento di autofill quando l&apos;operatore apre un cartellino vuoto.
                      </div>
                    </div>
                    <div>
                      <Label>Foglio Presenze Consulente (PDF)</Label>
                      <Select value={form.consultant_report_mode || "consuntivo_effettivo"} onValueChange={(v) => setForm({ ...form, consultant_report_mode: v })}>
                        <SelectTrigger data-testid="company-form-consultant-report-mode"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="consuntivo_effettivo">Consuntivo effettivo (ore reali)</SelectItem>
                          <SelectItem value="orario_teorico_eccezioni">Orario teorico + eccezioni</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="text-[11px] text-suite-neutral-500 mt-1">
                        Modalità di generazione del PDF inviato al consulente paghe.
                      </div>
                    </div>
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer pt-2 border-t border-suite-neutral-200">
                    <input type="checkbox" checked={Boolean(form.notify_external_consultant)}
                      onChange={(e) => setForm({ ...form, notify_external_consultant: e.target.checked })}
                      className="mt-1 h-4 w-4 accent-emerald-700"
                      data-testid="company-notify-external-consultant"
                    />
                    <span>
                      <span className="block text-sm font-semibold text-suite-neutral-800">Notifica il consulente esterno</span>
                      <span className="block text-xs text-suite-neutral-500 mt-0.5">
                        Invia una sola notifica quando il cartellino mensile viene chiuso definitivamente.
                      </span>
                    </span>
                  </label>
                </div>
              </fieldset>
            </SuiteTabsContent>

            {/* ============== TAB CONTRATTI E ISTITUTI ============== */}
            <SuiteTabsContent value="contratti" className="mt-0">
              {editingId ? (
                <CompanyContractsPanel companyId={editingId} readOnly={isExternalConsultant} />
              ) : (
                /* P6.2 · placeholder esplicito in creazione (parità con tab Accessi) */
                <div className="text-sm text-suite-neutral-500 px-3 py-6" data-testid="company-tab-contratti-placeholder">
                  Salva prima l&apos;anagrafica dell&apos;azienda per poter configurare contratti e iscrizioni istituzionali.
                </div>
              )}
            </SuiteTabsContent>

            {/* ============== TAB ACCESSI AZIENDALI ============== */}
            <SuiteTabsContent value="accessi" className="mt-0">
              {editingId && !isExternalConsultant ? (
                <CompanyAccessSection companyId={editingId} currentUser={user} />
              ) : (
                <div className="text-sm text-suite-neutral-500 px-3 py-6">
                  Salva prima l&apos;anagrafica dell&apos;azienda per gestire gli accessi.
                </div>
              )}
            </SuiteTabsContent>

            {/* ============== TAB BRANDING / PORTALE (P2) ============== */}
            <SuiteTabsContent value="portale" className="mt-0">
              <fieldset disabled={isExternalConsultant} className="space-y-5 disabled:opacity-100">
                {/* Identità visiva aziendale (promossa da Operatività · P2) */}
                <div className="rounded-lg border border-suite-neutral-200 p-4 space-y-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-suite-neutral-800">
                    <Palette size={14} className="text-suite-primary" /> Identità visiva aziendale
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Label>Logo aziendale (URL o data-URI)</Label>
                      <Input placeholder="https://... oppure data:image/png;base64,..."
                        value={form.logo_url || ""}
                        onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                        data-testid="company-form-logo-url"
                      />
                      <div className="text-[11px] text-suite-neutral-500 mt-1">Usato in dashboard, report e PDF. Non appare in login.</div>
                    </div>
                    <div>
                      <Label>Colore principale (hex)</Label>
                      <div className="flex items-center gap-2">
                        <Input placeholder="#065f46" value={form.primary_color || ""} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} data-testid="company-form-primary-color" />
                        {form.primary_color && <div className="w-8 h-8 rounded border border-suite-neutral-200" style={{ backgroundColor: form.primary_color }} />}
                      </div>
                      <div className="text-[11px] text-suite-neutral-500 mt-1">Colore che identifica l&apos;azienda nei report e nelle intestazioni.</div>
                    </div>
                    <div>
                      <Label>Colore secondario (opzionale)</Label>
                      <div className="flex items-center gap-2">
                        <Input placeholder="#0f766e" value={form.custom_settings?.secondary_color || ""} onChange={(e) => setForm({ ...form, custom_settings: { ...form.custom_settings, secondary_color: e.target.value } })} data-testid="company-form-secondary-color" />
                        {form.custom_settings?.secondary_color && <div className="w-8 h-8 rounded border border-suite-neutral-200" style={{ backgroundColor: form.custom_settings.secondary_color }} />}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Portale dedicato dipendenti */}
                <div className="rounded-lg border border-suite-neutral-200 p-4 space-y-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-suite-neutral-800">
                    <Link2 size={14} className="text-suite-primary" /> Portale dipendenti
                  </div>
                  <p className="text-xs text-suite-neutral-500">
                    Il link personalizzato mostrerà logo, denominazione e colori direttamente al dipendente prima del login.
                  </p>

                  <div>
                    <Label>Slug portale (URL)</Label>
                    <div className="flex items-center gap-2">
                      <Input placeholder="es. rossi-trasporti"
                        value={form.portal_slug || ""}
                        onChange={(e) => setForm({ ...form, portal_slug: toSlug(e.target.value) })}
                        data-testid="company-form-portal-slug"
                      />
                      {editingId && form.portal_slug && (
                        <SuiteButton type="button" variant="secondary" size="sm" onClick={() => copyPortalLink(form.portal_slug)} data-testid="copy-portal-link-btn" icon={Copy}>
                          Copia link
                        </SuiteButton>
                      )}
                    </div>
                    {/* P6.4 · anteprima link solo in edit (in creazione sarebbe un URL fittizio) */}
                    {editingId && (
                      <div className="text-[11px] text-suite-neutral-500 mt-1 flex items-center gap-1">
                        <Link2 size={11} />
                        Anteprima link:{" "}
                        <code className="px-1 py-0.5 rounded bg-suite-neutral-100">
                          {window.location.origin}/login/{form.portal_slug || toSlug(form.name) || "azienda"}
                        </code>
                      </div>
                    )}
                    <div className="text-[11px] text-amber-700 mt-1">
                      Se lasciato vuoto verrà generato automaticamente dal nome. Modificarlo invalida i vecchi link.
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Titolo del portale</Label>
                      <Input placeholder="Area Presenze e Banca Ore" value={form.custom_settings?.portal_title || ""} onChange={(e) => setForm({ ...form, custom_settings: { ...form.custom_settings, portal_title: e.target.value } })} data-testid="company-form-portal-title" />
                    </div>
                    <div>
                      <Label>Sottotitolo del portale</Label>
                      <Input placeholder="Portale riservato ai dipendenti" value={form.custom_settings?.portal_subtitle || ""} onChange={(e) => setForm({ ...form, custom_settings: { ...form.custom_settings, portal_subtitle: e.target.value } })} data-testid="company-form-portal-subtitle" />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Immagine di sfondo login (URL, opzionale)</Label>
                      <Input placeholder="https://..." value={form.custom_settings?.login_background_url || ""} onChange={(e) => setForm({ ...form, custom_settings: { ...form.custom_settings, login_background_url: e.target.value } })} data-testid="company-form-login-bg" />
                      <div className="text-[11px] text-suite-neutral-500 mt-1">
                        Non obbligatorio. Se assente viene usato il gradiente dei colori aziendali.
                      </div>
                    </div>
                  </div>
                </div>

                {/* Note Portale (P0.1 · separate da Note anagrafiche) */}
                <div>
                  <Label>Note Portale</Label>
                  <Textarea
                    value={form.custom_settings?.portal_notes || ""}
                    onChange={(e) => setForm({ ...form, custom_settings: { ...form.custom_settings, portal_notes: e.target.value } })}
                    rows={2}
                    data-testid="company-form-portal-notes"
                  />
                  <div className="text-[11px] text-suite-neutral-500 mt-1">
                    Note private sul portale (comunicazioni interne o configurazione). Non visibili al dipendente.
                  </div>
                </div>
              </fieldset>
            </SuiteTabsContent>
          </SuiteTabs>
        </div>
      </SuiteFormDialog>
    </div>
  );
}
